import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useMemo, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';

import { Stack } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/system/Unstable_Grid';
import { MobileDatePicker } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  Dialog,
  Divider,
  MenuItem,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress
} from '@mui/material';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { JobRecruitmentType } from 'src/services/jobs/jobs.types';
import { getUpdateDealErrorMessage } from 'src/services/deal-flow/deal-flow.utils';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { DealType, IDealStage, UpdateDealDto } from 'src/services/deal-flow/deal-flow.types';
import { useGetDealQuery, useGetPipelineQuery, useCreateDealMutation, useUpdateDealMutation } from 'src/services/deal-flow/deal-flow.service';

import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

interface FormValuesProps {
  title: string;
  description?: string;
  type: DealType;
  value?: string | number;
  close_date?: Date | null;
  stage_id?: string;
  // Job details
  job_title?: string;
  job_description?: string;
  job_vacancies?: string | number;
  job_recruitment_type?: JobRecruitmentType | '';
}

interface Props {
  open: boolean;
  onClose: VoidFunction;
  pipelineId: string;
  dealId?: string;
}

export default function DealEditorModal({
  open,
  onClose,
  pipelineId,
  dealId
}: Props) {

  const dispatch = useAppDispatch();

  const { t } = useTranslate();

  const { canAccess } = useFeatures();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.DEAL,
    entityId: dealId || undefined,
  });

  const [createDeal, { isLoading: isCreating }] = useCreateDealMutation();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateDealMutation();

  const { data: pipelineData } = useGetPipelineQuery({ pipelineId }, { skip: !pipelineId });

  const { data: dealData } = useGetDealQuery({
    dealId: dealId as string
  }, { skip: !dealId || !pipelineData }); // wait till pipelineData is loaded

  const pipelineStages = useMemo(() => {
    if (!pipelineData || !Array.isArray(pipelineData.stages)) return [];
    return pipelineData.stages.slice().sort((a: IDealStage, b: IDealStage) => a.order - b.order);
  }, [pipelineData]);

  const DealEditorSchema = Yup.object().shape({
    title: Yup.string().required(t('validation.required')),
    description: Yup.string(),
    type: Yup.mixed<DealType>().required(t('validation.required')),
    value: Yup.mixed<string | number>()
      .transform((value) => Number.isNaN(Number(value)) ? 0 : Number(value))
      .test('min', t('validation.min_integer', { min: 0 }), (value) =>
        value === '' || Number(value) >= 0
      ),
    close_date: Yup.date()
      .nullable()
      .test('future-date', t('validation.future_date'), (value) =>
        !value || new Date(value) > new Date()
      ),
    stage_id: Yup.string(),
    // Job details validation
    job_title: Yup.string(),
    job_description: Yup.string(),
    job_vacancies: Yup.mixed<string | number>()
      .transform((value) => Number.isNaN(Number(value)) ? undefined : Number(value))
      .test('min', t('validation.min_integer', { min: 0 }), (value) =>
        value === '' || value === undefined || Number(value) >= 0
      ),
    job_recruitment_type: Yup.mixed<JobRecruitmentType | ''>(),
  });

  const defaultValues = useMemo(
    () => ({
      title: dealData?.title || '',
      description: dealData?.description || '',
      type: dealData?.type || DealType.NEW,
      value: dealData?.value || '' as string | number,
      close_date: dealData?.close_date ? new Date(`${dealData.close_date}`) : null as Date | any,
      stage_id: dealData?.stage.id || undefined,
      // Job details
      job_title: dealData?.job_title || '',
      job_description: dealData?.job_description || '',
      job_vacancies: dealData?.job_vacancies || '' as string | number,
      job_recruitment_type: dealData?.job_recruitment_type || '' as JobRecruitmentType | '',
    }),
    [dealData]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(DealEditorSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const closeDate = watch('close_date');

  // Initialize form methods with default values and validation schema
  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
    }
  }, [open, defaultValues, methods]);

  const onSubmit = async (data: FormValuesProps) => {
    try {

      // Validate custom fields first
      const customFieldValidation = customFields.validate();
      if (!customFieldValidation.isValid) {
        return;
      }

      if (dealId) {
        const updateData: UpdateDealDto = {
          title: data.title,
          description: data.description,
          type: data.type,
          value: Number(data.value) || 0,
          close_date: data.close_date ? new Date(data.close_date) : undefined,
          stage_id: data.stage_id || undefined,
          // Job details
          job_title: data.job_title || undefined,
          job_description: data.job_description || undefined,
          job_vacancies: data.job_vacancies ? Number(data.job_vacancies) : undefined,
          job_recruitment_type: data.job_recruitment_type || undefined,
          custom_fields: customFields.getData()
        };


        await updateDeal({ dealId, data: updateData }).unwrap();

        enqueueSnackbar(t('deal_flow.api.update_deal.success'), { variant: 'success' });

      } else {
        await createDeal({
          title: data.title,
          description: data.description,
          type: data.type,
          value: Number(data.value) || 0,
          pipeline_id: pipelineId,
          close_date: data.close_date ? new Date(data.close_date) : undefined,
          stage_id: data.stage_id || undefined,
          // Job details
          job_title: data.job_title || undefined,
          job_description: data.job_description || undefined,
          job_vacancies: data.job_vacancies ? Number(data.job_vacancies) : undefined,
          job_recruitment_type: data.job_recruitment_type || undefined,
          custom_fields: customFields.getData(),
        }).unwrap();

        enqueueSnackbar(t('deal_flow.api.create_deal.success'), { variant: 'success' });

      }

      dispatch(rootApi.util.invalidateTags([{ type: 'PipelineDeals', id: pipelineId }, { type: 'MyPipelineDeals', id: pipelineId }]));

      reset();
      onClose();
    } catch (error) {

      let errorMsg = t('deal_flow.api.create_deal.default_error');
      if (dealId) {
        errorMsg = getUpdateDealErrorMessage(error);
      }

      enqueueSnackbar(errorMsg, { variant: 'error' });

    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const title = useMemo(() => {
    if (dealId) {
      return t('deal_flow.deal_editor.edit_title');
    }

    return t('deal_flow.deal_editor.create_title');
  }, [dealId, t]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {
          canAccess(FeatureType.DEAL_FLOW_ENABLED) ? (
            <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Box sx={{ my: 2 }}>
                {/* Deal Details */}
                <RHFSelect
                  name="type"
                  label={t('deal_flow.deal_editor.deal_type')}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value={DealType.NEW}>{t('deal_flow.deal_editor.new_deal')}</MenuItem>
                  <MenuItem value={DealType.EXISTING}>{t('deal_flow.deal_editor.existing_deal')}</MenuItem>
                </RHFSelect>

                <RHFTextField
                  name="title"
                  label={t('deal_flow.deal_editor.title')}
                  sx={{ mb: 2 }}
                />

                <RHFTextField
                  name="description"
                  label={t('deal_flow.deal_editor.description')}
                  multiline
                  rows={4}
                  sx={{ mb: 2 }}
                />

                <RHFTextField
                  name="value"
                  label={t('deal_flow.deal_editor.value')}
                  type="number"
                  InputProps={{
                    startAdornment: <Box component="span" sx={{ mr: 1 }}>$</Box>,
                  }}
                  sx={{ mb: 2 }}
                />

                <Stack direction="column" justifyItems="start" sx={{ mb: 2 }}>
                  <MobileDatePicker
                    name="date"
                    label={t('deal_flow.deal_editor.close_date')}
                    value={closeDate}
                    minDate={new Date()}
                    onChange={(newValue) => {
                      setValue('close_date', newValue);
                    }}
                    slotProps={{
                      actionBar: {
                        actions: ['clear', 'cancel', 'today', 'accept'],
                      }
                    }}
                  />
                  {
                    errors.close_date?.message && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'error.main', mt: 1, ml: 1 }}

                      >
                        {errors.close_date.message}
                      </Typography>

                    )
                  }
                </Stack>

                <RHFSelect
                  name="stage_id"
                  label={t('deal_flow.deal_editor.stage')}
                  placeholder={t('deal_flow.deal_editor.stage_placeholder')}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">{t('deal_flow.deal_editor.stage_placeholder')}</MenuItem>
                  {pipelineStages.map((stage: IDealStage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))}
                </RHFSelect>

                {/* Job Details */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box component="h3" sx={{ typography: 'subtitle1', mb: 2 }}>
                      {t('deal_flow.deal_editor.job_details')}
                    </Box>

                    <RHFTextField
                      name="job_title"
                      label={t('deal_flow.deal_editor.job_title')}
                      sx={{ mb: 2 }}
                    />

                    <RHFTextField
                      name="job_description"
                      label={t('deal_flow.deal_editor.job_description')}
                      multiline
                      rows={4}
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <RHFTextField
                        name="job_vacancies"
                        label={t('deal_flow.deal_editor.job_vacancies')}
                        type="number"
                        sx={{ flex: 1 }}
                      />

                      <RHFSelect
                        name="job_recruitment_type"
                        label={t('deal_flow.deal_editor.job_recruitment_type')}
                        sx={{ flex: 1 }}
                      >
                        <MenuItem value="">{t('common.none')}</MenuItem>
                        <MenuItem value={JobRecruitmentType.CONTINGENT}>{t('deal_flow.deal_editor.recruitment_type.contingent')}</MenuItem>
                        <MenuItem value={JobRecruitmentType.EXCLUSIVE}>{t('deal_flow.deal_editor.recruitment_type.exclusive')}</MenuItem>
                        <MenuItem value={JobRecruitmentType.INTERNAL}>{t('deal_flow.deal_editor.recruitment_type.internal')}</MenuItem>
                      </RHFSelect>
                    </Box>
                  </Box>
                </Box>

                {/* Custom Fields */}
                {customFields.hasCustomFields && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box component="h3" sx={{ typography: 'subtitle1', mb: 2 }}>
                      {t('common.custom_fields')}
                    </Box>
                    <Grid container gap={2}>
                      {customFields.renderCustomFields}
                    </Grid>
                  </>
                )}
              </Box>
            </DialogContent>
          ) : (
            <FeatureLockedListView
              text={t('billing.subscription_required')}
              onNavigate={handleClose}
            />
          )
        }

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t('common.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting || isCreating || isUpdating}
            disabled={isSubmitting || isCreating || isUpdating || !canAccess(FeatureType.DEAL_FLOW_ENABLED)}
            startIcon={isCreating || isUpdating ? <CircularProgress size={20} /> : null}
          >
            {dealId ? t('common.update') : t('common.create')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}